/******************************************
 * tenant data using MTB-API
 * 03/08/2021 - Noël Thoelen - v1
 */
import { AxiosResponse, AxiosError } from "axios";
import { mtbService } from "./mtbService";
import { rethrowRequestError } from "./request-errors";
import {
  ErrorCode,
  ITenantsForUserResponse,
  ITenantInfoType,
  IAppDataType,
  ITourguideJoinInviteType,
  ITourguideJoinInviteRedeemPayload,
  ITourguideJoinInviteRedeemResponse,
  ITeamRosterResponse,
  ITeamUserDeleteImpact,
  ITeamUserDeleteResult,
  ITeamUserType,
  ITeamUserUpdatePayload,
  EnumTenantUserStatus,
  EnumUserRole,
  NotFoundError,
  InvalidDataError,
  InSufficientTenantRights,
} from "..";

export class InvalidTenantError extends Error {}

export class TenantService {
  /**************************************
   * @description get info for a specific tenant
   * @link : ../api/config/tenantinfo?key={?}
   * @see http://localhost:3001/docs/#/config/tenantInfo?key={?}
   *
   * @param key tenant key of the tenant to be fetched
   * @Returns ITenantInfoType
   *
   * #### errors - check instanceOf error to give corresponding feedback to the user
   * - **NotFoundError** | unknown tenant
   * - **general Error** | something went wrong (probably network issues)
   */
  public async getTenantInfo(tenantKey: string): Promise<ITenantInfoType> {
    try {
      const json: AxiosResponse = await mtbService.getUnProtected(
        `/config/tenantInfo?key=${tenantKey}`
      );
      const tenantInfo = json.data as ITenantInfoType;
      return tenantInfo;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.NotFound:
          throw new NotFoundError();
        default:
          break;
      }
      rethrowRequestError(errResponse);
    }
  }

  /**************************************
   * @description get all tenants this user is involved in
   * @link : ../api/user/GetTenantsForUser
   * @see http://localhost:3001/docs/#/Configuration/GetTenantsForUser
   *
   * @param searchVal Optional string used as search key to filter list of tenants
   * @Returns ITenantsForUserResponse
   *
   * #### errors - check instanceOf error to give corresponding feedback to the user
   * - **general Error** | something went wrong (probably network issues)
   */
  public async getTenantsForUser(
    searchVal?: string
  ): Promise<ITenantsForUserResponse | void> {
    try {
      let searchParam = "";
      if (searchVal) {
        searchParam = `?searchVal=${searchVal}`;
      }
      const json: AxiosResponse = await mtbService.getProtected(
        `/user/getTenantsForUser${searchParam}`
      );
      const tenants = json.data as ITenantsForUserResponse;
      return tenants;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        default:
          break;
      }
      throw new Error((errResponse as Error).message);
    }
  }

  /***************************************
   * @description Using this method, the guestApp can fetch all relative data
   *    to fill the context containing all relative info for the app to work
   * @link : ../api/tenant/kkk/area (post/put)
   * @see http://localhost:3001/docs/#/appInfo/GetAppData
   *
   * @param tenantKey The tenantKey of the current tenant (stored in Context)
   * @param destinationId destinationId where the app in initalized for
   * @param tripCode Optional - a trip code
   * @Returns IAppData
   *
   * #### errors - check instanceOf error to give corresponding feedback to the user
   * - InSufficientTenantRights | a token needs to be provided
   * - InvalidTenantError | wrong tenant
   * - general Error | something went wrong (probably network issues)
   */
  public async getTenantAppData(
    tenantKey: string,
    alias?: string,
    tripCode?: string
  ): Promise<IAppDataType> {
    try {
      let json: AxiosResponse;
      if (!tripCode) {
        json = await mtbService.getProtected(
          `/tenant/${tenantKey}/appInfo?alias=${alias}`
        );
      } else {
        json = await mtbService.getProtected(
          `/tenant/${tenantKey}/appInfo?tripcode=${tripCode}`
        );
      }
      const appData = json.data as IAppDataType;
      return appData;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.InvalidTenantError:
          throw new InvalidTenantError();
        default:
          break;
      }
      rethrowRequestError(errResponse);
    }
  }

  /**************************************
   * @description Get the current tourguide team invite link (if live)
   * @link : ../api/tenant/{tenantKey}/tourguide-join-invite
   *
   * @param tenantKey The tenantKey of the current tenant
   * @Returns ITourguideJoinInviteType
   *
   * #### errors
   * - **NotFoundError** | no live invite token
   * - **InSufficientTenantRights** | TENANTADMIN+ only
   */
  public async getTourguideJoinInvite(
    tenantKey: string
  ): Promise<ITourguideJoinInviteType> {
    try {
      const json: AxiosResponse = await mtbService.getProtected(
        `/tenant/${tenantKey}/tourguide-join-invite`
      );
      return json.data as ITourguideJoinInviteType;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.NotFound:
          throw new NotFoundError();
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.DataInconsistancy:
          throw new InvalidDataError();
        default:
          break;
      }
      rethrowRequestError(errResponse);
    }
  }

  /**************************************
   * @description Generate or regenerate the tourguide team invite link
   * @link : ../api/tenant/{tenantKey}/tourguide-join-invite (POST)
   *
   * @param tenantKey The tenantKey of the current tenant
   * @Returns ITourguideJoinInviteType
   *
   * #### errors
   * - **InSufficientTenantRights** | TENANTADMIN+ only
   */
  public async createTourguideJoinInvite(
    tenantKey: string
  ): Promise<ITourguideJoinInviteType> {
    try {
      const json: AxiosResponse = await mtbService.postPutProtected(
        "post",
        `/tenant/${tenantKey}/tourguide-join-invite`,
        "{}"
      );
      return json.data as ITourguideJoinInviteType;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.NotFound:
          throw new NotFoundError();
        case ErrorCode.DataInconsistancy:
          throw new InvalidDataError();
        default:
          break;
      }
      rethrowRequestError(errResponse);
    }
  }

  /**************************************
   * @description Redeem a live Team invite for the current bearer (4.1c)
   * @link : ../api/tenant/{tenantKey}/tourguide-join-invite/redeem
   *
   * @param tenantKey The tenantKey of the current tenant
   * @param invite Opaque invite secret from `?invite=`
   * @Returns ITourguideJoinInviteRedeemResponse
   *
   * #### errors
   * - **InvalidDataError** | missing invite body
   * - **InSufficientTenantRights** | unverified / wrong tenant bearer
   */
  public async redeemTourguideJoinInvite(
    tenantKey: string,
    invite: string
  ): Promise<ITourguideJoinInviteRedeemResponse> {
    try {
      const payload: ITourguideJoinInviteRedeemPayload = { invite };
      const json: AxiosResponse = await mtbService.postPutProtected(
        "post",
        `/tenant/${tenantKey}/tourguide-join-invite/redeem`,
        JSON.stringify(payload)
      );
      return json.data as ITourguideJoinInviteRedeemResponse;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        ?.errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
        case ErrorCode.NoTokenSpecified:
          throw new InSufficientTenantRights();
        case ErrorCode.InvalidPayload:
          throw new InvalidDataError();
        default:
          break;
      }
      rethrowRequestError(errResponse);
    }
  }

  /**************************************
   * @description Paginated Team roster for admin Team page
   * @link : ../api/tenant/{tenantKey}/tenantUser/team
   */
  public async getTeamRoster(
    tenantKey: string,
    opts?: {
      status?: EnumTenantUserStatus;
      role?: EnumUserRole;
      search?: string;
      page?: number;
      pageItems?: number;
    }
  ): Promise<ITeamRosterResponse> {
    try {
      const json: AxiosResponse = await mtbService.getProtected(
        `/tenant/${tenantKey}/tenantUser/team`,
        {
          status: opts?.status,
          role: opts?.role,
          search: opts?.search,
          page: opts?.page,
          pageItems: opts?.pageItems,
        }
      );
      return json.data as ITeamRosterResponse;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.NotFound:
          throw new NotFoundError();
        default:
          break;
      }
      rethrowRequestError(errResponse);
    }
  }

  /**************************************
   * @description Single team member with Auth0 contact fields (Edit modal)
   * @link : ../api/tenant/{tenantKey}/tenantUser/team/{userId} (GET)
   */
  public async getTeamUser(
    tenantKey: string,
    userId: string
  ): Promise<ITeamUserType> {
    try {
      const json: AxiosResponse = await mtbService.getProtected(
        `/tenant/${tenantKey}/tenantUser/team/${encodeURIComponent(userId)}`
      );
      return json.data as ITeamUserType;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.NotFound:
          throw new NotFoundError();
        default:
          break;
      }
      rethrowRequestError(errResponse);
    }
  }

  /**
   * @description Preflight for delete confirm (FK refs → inactive warning)
   * @link : ../api/tenant/{tenantKey}/tenantUser/team/{userId}/delete-impact (GET)
   */
  public async getTeamUserDeleteImpact(
    tenantKey: string,
    userId: string
  ): Promise<ITeamUserDeleteImpact> {
    try {
      const json: AxiosResponse = await mtbService.getProtected(
        `/tenant/${tenantKey}/tenantUser/team/${encodeURIComponent(userId)}/delete-impact`
      );
      return json.data as ITeamUserDeleteImpact;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.NotFound:
          throw new NotFoundError();
        default:
          break;
      }
      rethrowRequestError(errResponse);
    }
  }

  /**************************************
   * @description Approve (ACTIVE) or make inactive a team member
   * @link : ../api/tenant/{tenantKey}/tenantUser/team/{userId}/status (PATCH)
   */
  public async updateTeamUserStatus(
    tenantKey: string,
    userId: string,
    status: EnumTenantUserStatus.ACTIVE | EnumTenantUserStatus.INACTIVE
  ): Promise<ITeamUserType> {
    try {
      const json: AxiosResponse = await mtbService.patchProtected(
        `/tenant/${tenantKey}/tenantUser/team/${encodeURIComponent(userId)}/status`,
        JSON.stringify({ status })
      );
      return json.data as ITeamUserType;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.NotFound:
          throw new NotFoundError();
        case ErrorCode.InvalidPayload:
          throw new InvalidDataError();
        default:
          break;
      }
      rethrowRequestError(errResponse);
    }
  }

  /**************************************
   * @description Edit team member names/role/reactivate
   * @link : ../api/tenant/{tenantKey}/tenantUser/team/{userId} (PATCH)
   */
  public async updateTeamUser(
    tenantKey: string,
    userId: string,
    payload: ITeamUserUpdatePayload
  ): Promise<ITeamUserType> {
    try {
      const json: AxiosResponse = await mtbService.patchProtected(
        `/tenant/${tenantKey}/tenantUser/team/${encodeURIComponent(userId)}`,
        JSON.stringify(payload)
      );
      return json.data as ITeamUserType;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.NotFound:
          throw new NotFoundError();
        case ErrorCode.InvalidPayload:
          throw new InvalidDataError();
        default:
          break;
      }
      rethrowRequestError(errResponse);
    }
  }

  /**************************************
   * @description Delete team member (unused → 204; used → inactivated 200)
   * @link : ../api/tenant/{tenantKey}/tenantUser/team/{userId} (DELETE)
   * @returns null when hard-deleted (204); ITeamUserDeleteResult when inactivated
   */
  public async deleteTeamUser(
    tenantKey: string,
    userId: string
  ): Promise<ITeamUserDeleteResult | null> {
    try {
      const json: AxiosResponse = await mtbService.deleteProtected(
        `/tenant/${tenantKey}/tenantUser/team/${encodeURIComponent(userId)}`
      );
      if (json.status === 204 || json.data == null || json.data === "") {
        return null;
      }
      return json.data as ITeamUserDeleteResult;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.NotFound:
          throw new NotFoundError();
        case ErrorCode.InvalidPayload:
          throw new InvalidDataError();
        default:
          break;
      }
      rethrowRequestError(errResponse);
    }
  }
}
